/**
 * Web listening indicator — the twin of `NovaListening.tsx`.
 *
 * Sits inside the composer's input slot while voice input runs: Nova, a
 * shimmering status label and a small level meter, on the bar's own surface —
 * no pill of its own. No clock: this is dictation, not a voice memo. The eyes
 * are cut out through a mask, so the bar shows through them.
 *
 * Browsers report no mic volume to the speech API, so on web `level` is the
 * pulse `useVoiceInput` emits as recognised words arrive; it drives the same
 * `--nova-level` the native side feeds through Reanimated.
 */
import type { CSSProperties, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NovaIcon } from '../assistant-ui/nova-loader.web';
import { ShimmerText } from '../assistant-ui/shimmer-text.web';
import { VoiceLevelMeter } from './VoiceLevelMeter.web';

/** `listening` is the user talking; `transcribing` is the wait on the server. */
export type NovaListeningState = 'listening' | 'transcribing';

export interface NovaListeningProps {
  state: NovaListeningState;
  /** 0..1 mic amplitude. Omit where no meter exists. */
  level?: number;
  /** Overrides the default status label (e.g. a live interim transcript). */
  label?: string;
  /** Hide the label and meter, leaving only the face (e.g. beside live text). */
  faceOnly?: boolean;
  /** Hide the face, leaving the label (and meter) when the face is rendered separately. */
  hideFace?: boolean;
  /** Hide the level meter, for surfaces that show it beside their stop button instead. */
  hideMeter?: boolean;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

export const NovaListening: FC<NovaListeningProps> = ({
  state,
  level,
  label,
  faceOnly = false,
  hideFace = false,
  hideMeter = false,
  size = 22,
  className,
  style,
}) => {
  const { t } = useTranslation('common');
  const listening = state === 'listening';
  const showMeter = listening && !faceOnly && !hideMeter;
  const text =
    label ?? (listening ? t('voice.ptt.recording') : t('voice.ptt.transcribing'));

  return (
    <div className={['tabbio-voice-status', className].filter(Boolean).join(' ')} style={style}>
      {hideFace ? null : (
        <NovaIcon
          status={listening ? 'listening' : 'writing'}
          size={size}
          level={listening ? level : undefined}
        />
      )}
      {faceOnly ? null : (
        <ShimmerText
          className="tabbio-voice-status-label"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {text}
        </ShimmerText>
      )}
      {showMeter ? <VoiceLevelMeter level={level ?? 0} /> : null}
    </div>
  );
};

export default NovaListening;
